import { isFrontPageUrl, normalizePrivacyPolicyUrl } from '@tm/shared-lib/src/privacyPolicyUrl'
import { SurveyJson } from '@tm/types/survey/entities'

export const PRIVACY_POLICY_URL_ERROR_KEY = 'organizationEdit.privacyPolicyInvalid'
export const PRIVACY_POLICY_URL_FRONT_PAGE_WARNING_KEY = 'organizationEdit.privacyPolicyFrontPage'

/**
 * True when a privacy policy URL field holds something that is neither empty nor a URL. A field
 * that was never set is undefined in the survey JSON and in the legacy form's Immutable values, or
 * null in older stored data, and counts as empty.
 */
export function isInvalidPrivacyPolicyUrl(value: unknown): boolean {
  if (value === undefined || value === null) return false
  return typeof value !== 'string' || normalizePrivacyPolicyUrl(value) === null
}

/**
 * True when a privacy policy URL field holds a site's front page. The value saves, so the field
 * shows a warning instead of an error.
 */
export function isFrontPagePrivacyPolicyUrl(value: unknown): boolean {
  return typeof value === 'string' && isFrontPageUrl(value)
}

/**
 * "acme.fi/privacy" becomes "https://acme.fi/privacy". A value that is not a URL is returned as
 * is, so the field keeps showing what the user typed next to its error.
 */
export function normalizePrivacyPolicyUrlIfValid(value: string): string {
  return normalizePrivacyPolicyUrl(value) ?? value
}

// Some old surveys have no langs, although SurveyJson requires it
type SurveyPrivacyPolicyUrls = Partial<Pick<SurveyJson, 'langs'>> &
  Pick<SurveyJson, 'mainLang' | 'privacyPolicy' | 'privacyPolicyTranslations'>

/**
 * True when a privacy policy URL the survey editor shows is invalid: the main one, or the
 * translation of a survey language other than the main one, as PrivacyPolicyTranslations renders
 * them. A translation left behind by a removed language has no field to fix it in, so it does not
 * count.
 */
export function hasInvalidPrivacyPolicyUrl(data: SurveyPrivacyPolicyUrls): boolean {
  const translated = (data.langs ?? [])
    .filter(lang => lang !== data.mainLang)
    .map(lang => data.privacyPolicyTranslations?.[lang]?.privacyPolicy)
  return [data.privacyPolicy, ...translated].some(isInvalidPrivacyPolicyUrl)
}

/**
 * An old survey version being restored, with its invalid privacy policy URLs cleared so the survey
 * falls back to the organization's policy. Versions saved before URLs were validated may hold one,
 * the server refuses to save it, and the restore offers no field to correct it in.
 */
export function clearInvalidPrivacyPolicyUrls(data: SurveyJson): SurveyJson {
  const clear = <T>(value: T) => (isInvalidPrivacyPolicyUrl(value) ? '' : value)
  const translations = data.privacyPolicyTranslations
  return {
    ...data,
    privacyPolicy: clear(data.privacyPolicy),
    privacyPolicyTranslations:
      translations &&
      Object.fromEntries(
        Object.entries(translations).map(([lang, item]) => [
          lang,
          { ...item, privacyPolicy: clear(item.privacyPolicy) },
        ])
      ),
  }
}
